import { AddRepresentativeEmailPopup, AddRepresentativeEmailPopupProps } from "@/Appointments/Components/Calendar/AddRepresentativeEmailPopup";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { PatientEmailValidator } from "@/Common/Helpers/PatientEmailValidator";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { IPatientHistoryAppointmentProps } from "./AppointmentView";

class AppointmentEditPopupState {
    public isSaveDisabled: boolean;
    public showAddEmailPopup: boolean;
    public addEmailPopupProps: AddRepresentativeEmailPopupProps;
    public isTeleconsultation: boolean;

    public subject: string;
    public appointmentState: string;
    public notes: string;
    public isPaid: boolean;
    public isNotifyPatient: boolean;
    public isNotifyManualOverride: boolean;
    public isNotifyPatientCheckboxDisabled: boolean;

    public constructor(init?: Partial<AppointmentEditPopupState>) {
        Object.assign(this, init);
    }

    public static default(): AppointmentEditPopupState {
        return new AppointmentEditPopupState({
            isSaveDisabled: false,
            showAddEmailPopup: false,
            addEmailPopupProps: null,
            isTeleconsultation: false
        });
    }
}

class AppointmentEditPopup extends BaseReactComponent<IPatientHistoryAppointmentProps, AppointmentEditPopupState> {
    constructor(props: IPatientHistoryAppointmentProps) {
        super(props);

        const appointment = props.Model;
        const initialState = new AppointmentEditPopupState({
            ...AppointmentEditPopupState.default(),
            isTeleconsultation: appointment.get("IsTeleconsultation"),
            subject: appointment.get("Subject"),
            notes: appointment.get("NotesForEdit"),
            appointmentState: appointment.get("SelectedAppointmentStateCode"),
            isPaid: appointment.get("IsPaid"),
            isNotifyPatient: appointment.get("NotifyPatient"),
            isNotifyManualOverride: appointment.get("NotifyPatientManualOverride"),
            isNotifyPatientCheckboxDisabled: appointment.get("NotifyPatientDisabled")
        });
        this.state = initialState;
    }

    public componentDidMount(): void {
        this.setOverlay("AppointmentEditPopupOverlay", {
            transparent: true
        });
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        let appointmentId = this.props.Model.get("SelectedAppointmentId");
        let appts = this.props.Model.get("AppointmentList").toArray();
        let selectedAppointment = appts.filter(x => x.Model.AppointmentId == appointmentId).firstOrDefault();
        let isPortalAppointment = selectedAppointment != null && !!selectedAppointment.Model.IsSharedWithPortal;
        let popupStyle: React.CSSProperties = { left: "50%" };

        return (
            <div>
                <Popup
                    title={cacheStore.GetString("EDITLabel")}
                    style={popupStyle}
                    cssClass="double-booking"
                    closeTarget={LocalPatientOperations.History.Appointment_CancelModifyAppointment}
                >
                    <div className="p-body">
                        {isPortalAppointment && (
                            <div className="portal-appointment-edit-warning">
                                <span><i>{cacheStore.GetString("PortalAppointmentEditWarning")}</i></span>
                            </div>
                        )}
                        <div className="clearfix" style={{ marginBottom: "10px" }}>
                            <label>{cacheStore.GetString("SubjectLabel")}</label>
                            <TextboxControl
                                disabled={isPortalAppointment}
                                type="text"
                                value={this.state.subject}
                                onChange={value => this.mutateState(s => s.subject = value)}
                            />
                        </div>
                        <div className="clearfix" style={{ marginBottom: "10px" }}>
                            <label>{cacheStore.GetString("StateLabel")}</label>
                            <SelectLookupControl
                                Callback={value => this.mutateState(s => s.appointmentState = value)}
                                Value={this.state.appointmentState}
                                AvailableValues={this.props.Model.get("AppointmentStates")
                                    .toArray()
                                    .map(state => new ComboBoxDropDownItem(state.LookupCode, cacheStore.GetLocalizedValueByLookupId(state.LookupID)))
                                }
                            />
                        </div>
                        <div className="clearfix">
                            <span>{cacheStore.GetString("NotesDLabel")}</span>
                            <div className="text-right">
                                <TextAreaResizable
                                    id="task_body_box"
                                    text={this.state.notes}
                                    isDisabled={false}
                                    onChange={value => this.mutateState(s => s.notes = value)}
                                    setCursorStartOnFocus={false}
                                    resetTextOnIdChange={true}
                                />
                            </div>
                        </div>
                        {cacheStore.ClinicSettings_Portal.EnablePortalNotifications == true && (
                            <div style={{ paddingTop: "5px" }}>
                                <CheckboxControl
                                    disabled={this.state.isNotifyPatientCheckboxDisabled}
                                    checked={this.state.isNotifyPatient}
                                    label={cacheStore.GetString("NotifyPatientLabel")}
                                    onChange={isChecked => this.updateNotifyPatientStatus(isChecked, this.state.isTeleconsultation)}
                                />
                            </div>
                        )}
                        <div style={{ paddingTop: "5px" }}>
                            <CheckboxControl
                                id="paid_check"
                                className="pull-right"
                                label={cacheStore.GetString("PaidLabel")}
                                onChange={isChecked => this.mutateState(s => s.isPaid = isChecked)}
                                checked={this.state.isPaid}
                            />
                        </div>
                        {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                            <div style={{ paddingTop: "5px" }}>
                                <CheckboxControl
                                    id="is_teleconsultation_check"
                                    className="pull-right"
                                    label={cacheStore.GetString("TeleconsultationLabel")}
                                    checked={this.state.isTeleconsultation}
                                    onChange={isChecked => this.updateNotifyPatientStatus(this.state.isNotifyManualOverride, isChecked)}
                                />
                            </div>
                        )}
                    </div>
                    <div className="p-footer">
                        <div className="flex-display flex-gap-xs flex-end">
                            <ButtonControl
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.History.Appointment_CancelModifyAppointment)}
                                label={cacheStore.GetString("CancelLabel")}
                                className="btn btn-default"
                            />
                            <ButtonControl
                                onClick={() => this.onSaveClick()}
                                label={cacheStore.GetString("SaveLabel")}
                                variant="brand"
                            />
                        </div>
                    </div>
                </Popup>

                {this.state.showAddEmailPopup &&
                    <AddRepresentativeEmailPopup {...this.state.addEmailPopupProps} />
                }
            </div>
        );
    }

    private async checkForValidEmailAndSave(): Promise<void> {
        try {
            if (cacheStore.ClinicSettings.EnableTelemedFeature
                && cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox
                && this.state.isTeleconsultation
            )
                await PatientHelper.ensurePatientHasValidEmail(
                    patientStore.getCurrentPatientContactId(),
                    async patientEmailValidator => this.openAddEmailPopup(patientEmailValidator)
                );

            this.saveAppointment();
        }
        catch (e) {
            console.error(e);
            this.mutateState(s => s.isSaveDisabled = false);
        }
    }

    private onSaveClick(): void {
        this.mutateState(
            s => s.isSaveDisabled = true,
            () => this.checkForValidEmailAndSave()
        );
    }

    private async openAddEmailPopup(patientEmailValidator: PatientEmailValidator): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mutateState(s => {
                s.addEmailPopupProps = this.initAddEmailPopupControlProps(
                    () => resolve(),
                    () => reject("Adding new email cancelled."),
                    patientEmailValidator
                );
                s.showAddEmailPopup = true;
            });
        });
    }

    private initAddEmailPopupControlProps(
        onSubmit: () => void,
        onCancel: () => void,
        patientEmailValidator: PatientEmailValidator
    ): AddRepresentativeEmailPopupProps {
        return {
            ...AddRepresentativeEmailPopupProps.defaultProps,
            representantsDto: patientEmailValidator.Representants,

            enablePhoneContent: false,

            patientUnderAgeWithoutRepresentantLabel: "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentWithoutRepresentativeLabel",

            patientWithoutRepresentantContactLabel: "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentLabel",

            onCancel: () => {
                this.mutateState(
                    s => {
                        s.showAddEmailPopup = false;
                        s.isSaveDisabled = false;
                    },
                    () => onCancel()
                );
            },

            onSubmit: () => {
                this.mutateState(
                    s => s.showAddEmailPopup = false,
                    () => onSubmit()
                );
            }
        };
    }

    private saveAppointment(): void {
        sessionStore.dispatch(LocalPatientOperations.History.Appointment_SaveModifyAppointment, { ...this.state });
    }

    private updateNotifyPatientStatus(isNotifyPatientManualOverrideChecked: boolean, isTeleconsultationChecked: boolean): void {
        this.mutateState(s => {
            s.isTeleconsultation = isTeleconsultationChecked;
            s.isNotifyManualOverride = isNotifyPatientManualOverrideChecked;
            const isNotifyAlreadyEnabled = this.props.Model.get("NotifyPatient");

            if (isNotifyAlreadyEnabled || (s.isTeleconsultation && cacheStore.ClinicSettings.EnableTelemedFeature)) {
                s.isNotifyPatient = true;
                s.isNotifyPatientCheckboxDisabled = true;
            }
            else {
                const patientHasEmail = patientStore.getCurrentPatient().Emails?.length > 0;
                const notifyPatient = patientHasEmail
                    && (s.isNotifyManualOverride ?? isNotifyAlreadyEnabled);

                s.isNotifyPatient = notifyPatient;
                s.isNotifyPatientCheckboxDisabled = !patientHasEmail;
            }
        });
    }
}

export { AppointmentEditPopup };
